import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Button } from "reactstrap";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import DatePicker from "react-datepicker";
import { Calendar, Download, Search } from "react-feather";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import "react-datepicker/dist/react-datepicker.css";
import { Image } from "../../AbstractElements";
import { dynamicImage } from "../../Utils";

const MasReportV3 = () => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState<Date>(thirtyDaysAgo);
  const [toDate, setToDate] = useState<Date>(today);

  const serverUrl = localStorage.getItem("serverUrl");

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchReportData = async () => {
    if (!fromDate || !toDate) return;
    try {
      setLoading(true);
      const params = {
        start_date: formatDate(fromDate),
        end_date: formatDate(toDate),
      };
      const url = `${serverUrl}${API.getMasReportV3}`;
      const { status, data } = await getData(url, true, params);

      if (status === 200 && (Array.isArray(data?.data) || Array.isArray(data))) {
        setReportData(data?.data || data);
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error(error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [fromDate, toDate, serverUrl]);

  useEffect(() => {
    const lower = searchText.toLowerCase();
    const filtered = reportData.filter((item) => {
      if (!item) return false;
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(lower)
      );
    });
    setDisplayedData(filtered);
  }, [searchText, reportData]);

  const keys = useMemo(() => {
    if (!reportData.length || !reportData[0]) return [];
    return Object.keys(reportData[0]);
  }, [reportData]);

  const captionTableHeader = useMemo(() => {
    return keys.map((key: string, index: number) => ({
      id: index.toString(),
      head: key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim().toUpperCase(),
      key: key,
    }));
  }, [keys]);

  const renderCellValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const subKeys = Object.keys(value[0]);
      return (
        <div style={{ minWidth: "260px" }}>
          {value.map((item: any, i: number) => (
            <div
              key={i}
              style={{
                background: i % 2 === 0 ? "rgba(var(--bs-primary-rgb), 0.06)" : "rgba(var(--bs-secondary-rgb), 0.04)",
                borderLeft: "3px solid var(--bs-primary)",
                borderRadius: "6px",
                padding: "5px 8px",
                marginBottom: i < value.length - 1 ? "5px" : 0,
                fontSize: "0.78rem",
              }}
            >
              {subKeys.map((sk) => (
                <div key={sk} style={{ display: "flex", gap: "4px", alignItems: "flex-start", lineHeight: "1.4" }}>
                  <span style={{ fontWeight: 600, color: "var(--bs-primary)", whiteSpace: "nowrap", textTransform: "capitalize" }}>
                    {sk.replace(/_/g, " ")}:
                  </span>
                  <span style={{ color: "var(--bs-body-color)" }}>{item[sk] ?? "-"}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    if (Array.isArray(value)) return value.join(", ") || "-";

    if (typeof value === "object") {
      return (
        <div style={{ fontSize: "0.78rem" }}>
          {Object.entries(value).map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: "4px" }}>
              <span style={{ fontWeight: 600, color: "var(--bs-primary)", textTransform: "capitalize" }}>
                {k.replace(/_/g, " ")}:
              </span>
              <span>{String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return String(value);
  };

  const handleExportToExcel = () => {
    if (displayedData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(displayedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `MAS_Report_V3_${formatDate(new Date())}.xlsx`);
  };

  return (
    <div className="page-body mt-5 p-2">
      <Card>
        <div className="d-flex justify-content-between align-items-center pt-4 pb-2 px-3">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="MAS Report V3"
            span="Report"
          />
        </div>

        <div className="row mb-4 px-3 my-2 align-items-center justify-content-between g-2">
          <div className="col-md-4">
            <div className="position-relative">
              <Input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                className="form-control ps-4"
              />
              <Search size={14} className="position-absolute translate-middle-y top-50 start-0 ms-2 text-muted" />
            </div>
          </div>

          <div className="col-md-8 d-flex align-items-center justify-content-end gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small text-muted">From</label>
              <DatePicker
                selected={fromDate}
                onChange={(date: Date | null) => date && setFromDate(date)}
                maxDate={new Date()}
                dateFormat="dd-MM-yyyy"
                customInput={
                  <input
                    className="form-control form-control-sm"
                    style={{ width: "120px", height: "35px" }}
                  />
                }
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small text-muted">To</label>
              <DatePicker
                selected={toDate}
                onChange={(date: Date | null) => date && setToDate(date)}
                maxDate={new Date()}
                dateFormat="dd-MM-yyyy"
                customInput={
                  <input
                    className="form-control form-control-sm"
                    style={{ width: "120px", height: "35px" }}
                  />
                }
              />
            </div>
            <Button
              color="primary"
              size="sm"
              className="d-flex align-items-center gap-2"
              onClick={handleExportToExcel}
              disabled={displayedData.length === 0}
            >
              <Download size={14} />
              <span>Export</span>
            </Button>
          </div>
        </div>

        <div className="px-3 pb-3">
          <div className="table-responsive" style={{ minHeight: "400px" }}>
            {loading ? (
              <div className="text-center py-5">
                <Image
                  className="b-r-10 img-80"
                  src={dynamicImage("dashboard/loader.gif")}
                  alt="Loading..."
                />
              </div>
            ) : (
              <CommonTableNew
                caption=""
                headData={captionTableHeader}
                data={displayedData}
                renderRow={(row, rowIndex) => {
                  if (!row) return <tr key={rowIndex}><td colSpan={keys.length}>-</td></tr>;
                  return (
                    <tr key={rowIndex}>
                      {keys.map((key, colIndex) => (
                        <td key={colIndex} style={{ verticalAlign: "top" }}>
                          {renderCellValue(row[key])}
                        </td>
                      ))}
                    </tr>
                  );
                }}
              />
            )}
            {!loading && displayedData.length === 0 && (
              <div className="text-center py-4 text-muted">
                No data found for the selected criteria.
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MasReportV3;
